import { useCallback, useMemo, useRef, type KeyboardEvent } from 'react';
import {
    Autocomplete,
    Button,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
    FilePathEntry,
    moveFilePathEntry,
    setPrimaryFilePathEntry,
    toFilePathEditorValueFromEntries,
    touchFilePathEntry,
} from '../utils/filePaths';
import { formatAppDateTime } from '../utils/dateFormats';
import { MarkerHighlightTextField } from '../utils/textMarkerHighlight';

const DEFAULT_VERSION_OPTIONS = ['5.4', '5.7'];

const isHttpUrl = (value: string): boolean => {
    try {
        const parsed = new URL(value.trim());
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

export type FilePathEntriesEditorProps = {
    entries: FilePathEntry[];
    onChange: (entries: FilePathEntry[]) => void;
    title?: string;
    versionOptions?: string[];
    pathPlaceholder?: string;
    defaultNewLabel?: string;
    /** Used when the last row is removed. */
    createEmptyEntry?: () => FilePathEntry;
    onBlur?: () => void;
    onCopy?: (text: string) => void | Promise<void>;
};

export const FilePathEntriesEditor = ({
    entries,
    onChange,
    title = 'File Path',
    versionOptions = DEFAULT_VERSION_OPTIONS,
    pathPlaceholder = 'I:\\My Drive\\FabAssets\\…',
    defaultNewLabel = '5.7',
    createEmptyEntry = () => ({ label: '', path: '' }),
    onBlur,
    onCopy,
}: FilePathEntriesEditorProps) => {
    const pathInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const options = useMemo(
        () => (versionOptions.length ? versionOptions : DEFAULT_VERSION_OPTIONS),
        [versionOptions],
    );

    const focusPathAt = useCallback((index: number) => {
        window.setTimeout(() => {
            pathInputRefs.current[index]?.focus();
        }, 0);
    }, []);

    const handleCopy = useCallback(
        async (value?: string | null) => {
            const text = (value ?? '').trim();
            if (!text) {
                return;
            }
            if (onCopy) {
                await onCopy(text);
                return;
            }
            try {
                await navigator.clipboard.writeText(text);
            } catch {
                /* ignore */
            }
        },
        [onCopy],
    );

    const handleRowChange = useCallback(
        (index: number, field: 'label' | 'path', value: string) => {
            const next = [...entries];
            const current = next[index] ?? createEmptyEntry();
            if ((current[field] ?? '') === value) {
                return;
            }
            next[index] = touchFilePathEntry({ ...current, [field]: value });
            onChange(next);
        },
        [createEmptyEntry, entries, onChange],
    );

    const handleAdd = useCallback(() => {
        const focusIndex = entries.length;
        onChange([...entries, { label: defaultNewLabel, path: '' }]);
        focusPathAt(focusIndex);
    }, [defaultNewLabel, entries, focusPathAt, onChange]);

    const handleRemove = useCallback(
        (index: number) => {
            const next = entries.filter((_, rowIndex) => rowIndex !== index);
            onChange(next.length ? next : [createEmptyEntry()]);
        },
        [createEmptyEntry, entries, onChange],
    );

    const handleMove = useCallback(
        (index: number, direction: -1 | 1) => {
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= entries.length) {
                return;
            }
            onChange(moveFilePathEntry(entries, index, targetIndex));
        },
        [entries, onChange],
    );

    const handleTogglePrimary = useCallback(
        (index: number) => {
            const current = entries[index];
            if (!current) {
                return;
            }
            onChange(setPrimaryFilePathEntry(entries, index, !current.primary));
        },
        [entries, onChange],
    );

    const handlePathKeyDown = useCallback(
        (index: number, event: KeyboardEvent) => {
            if (event.key !== 'Enter') {
                return;
            }
            event.preventDefault();
            const next = [...entries, { label: defaultNewLabel, path: '' }];
            onChange(next);
            focusPathAt(index + 1);
        },
        [defaultNewLabel, entries, focusPathAt, onChange],
    );

    const canCopyAll = entries.some(
        (row) => (row.path ?? '').trim() || (row.label ?? '').trim(),
    );

    return (
        <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Tooltip title="Add path row">
                    <Button
                        size="medium"
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAdd}
                        aria-label={`Add ${title}`}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}
                    >
                        {title}
                    </Button>
                </Tooltip>
                <Tooltip title="Copy all paths">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => void handleCopy(toFilePathEditorValueFromEntries(entries))}
                            disabled={!canCopyAll}
                            aria-label="Copy all paths"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {entries.map((entry, index) => (
                <Stack key={`file-path-row-${index}`} direction="row" spacing={0.5} alignItems="center">
                    <Tooltip
                        title={
                            entry.primary
                                ? 'Primary path (multiple allowed)'
                                : 'Mark as primary (multiple allowed)'
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                color={entry.primary ? 'warning' : 'default'}
                                onClick={() => handleTogglePrimary(index)}
                                aria-label={entry.primary ? 'Unmark primary path' : 'Mark primary path'}
                                aria-pressed={!!entry.primary}
                            >
                                {entry.primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Stack direction="column" spacing={0} sx={{ flexShrink: 0 }}>
                        <Tooltip title="Move up">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleMove(index, -1)}
                                    disabled={index === 0}
                                    aria-label="Move path up"
                                    sx={{ p: 0.25 }}
                                >
                                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleMove(index, 1)}
                                    disabled={index >= entries.length - 1}
                                    aria-label="Move path down"
                                    sx={{ p: 0.25 }}
                                >
                                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Autocomplete
                        freeSolo
                        options={options}
                        value={entry.label ?? ''}
                        onChange={(_, value) => handleRowChange(index, 'label', value ?? '')}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                                handleRowChange(index, 'label', value);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="5.7"
                                onBlur={onBlur}
                            />
                        )}
                        sx={{ width: 220, minWidth: 160, flexShrink: 0 }}
                    />
                    <Tooltip title="Copy path">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => void handleCopy(entry.path ?? '')}
                                disabled={!entry.path?.trim()}
                                aria-label="Copy path"
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {isHttpUrl(entry.path ?? '') && (
                        <Tooltip title="Open in new tab">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        window.open((entry.path ?? '').trim(), '_blank', 'noopener,noreferrer');
                                    }}
                                    aria-label="Open path in new tab"
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <MarkerHighlightTextField
                        fullWidth
                        size="small"
                        placeholder={pathPlaceholder}
                        value={entry.path}
                        onChange={(event) => handleRowChange(index, 'path', event.target.value)}
                        onBlur={onBlur}
                        onKeyDown={(event) => handlePathKeyDown(index, event)}
                        inputRef={(element) => {
                            pathInputRefs.current[index] = element;
                        }}
                    />
                    {!!entry.timestamp?.trim() && (
                        <Tooltip title={`Edited ${formatAppDateTime(entry.timestamp) || entry.timestamp}`}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    flexShrink: 0,
                                    minWidth: 88,
                                    maxWidth: 120,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    userSelect: 'none',
                                }}
                            >
                                {formatAppDateTime(entry.timestamp) || entry.timestamp}
                            </Typography>
                        </Tooltip>
                    )}
                    <Tooltip title="Remove row">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemove(index)}
                                disabled={
                                    entries.length === 1
                                    && !entry.label?.trim()
                                    && !entry.path?.trim()
                                }
                                aria-label="Remove path row"
                            >
                                <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            ))}
        </Stack>
    );
};
